package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable

/*
 * SH-47: who needs supervision (SH-104: who leads a shift is [ShiftLeadRule]), and whether a person's occupational medical
 * exam (badania lekarskie medycyny pracy, art. 229 KP) and OHS training (szkolenie BHP, art. 237³
 * KP) are still valid.
 *
 * Where it lives (docs/compliance/audit-2026-10-10.md, section 6):
 * - competence: on the engagement (users/{uid}.engagements.{wpId}), which
 *   colleagues and managers read for scheduling, like the role and skills;
 * - the dates: users/{uid}/hr/{wpId} ([StaffRecord]), read by the person and the workplace's
 *   owner and financial managers only. Dates and validity only: never results, restrictions,
 *   diagnoses or scans (a medical certificate's content is health data, GDPR Art. 9);
 * - operational managers see only a status per check ([StaffReadiness],
 *   workplaces/{wpId}/readiness/{uid}), written with the dates and refreshed daily by
 *   functions/staffReadiness.js;
 * - availability notes: workplaces/{wpId}/staff_notes/{uid}, read by the person and the
 *   workplace's managers (who schedule).
 */

/** How independently a person works at a workplace. */
@Serializable
enum class Competence {
    /** Works on their own (samodzielny). */
    INDEPENDENT,
    /**
     * Never works alone (wymaga nadzoru): whenever they work, someone who does not need
     * supervision works at the same time (SH-104, [SupervisionCheck]).
     */
    NEEDS_SUPERVISION
}

/** A dated requirement for working at all. */
enum class StaffCheck {
    /** Occupational medical exam (art. 229 KP). */
    MEDICAL_EXAM,
    /** OHS training (art. 237³ KP). */
    OHS_TRAINING
}

/** Where a [StaffCheck] stands on a day. */
enum class CheckStatus {
    VALID,
    /** Valid, but ends within [StaffChecks.WARNING_DAYS] days. */
    EXPIRING,
    EXPIRED,
    /** No date recorded. */
    MISSING
}

/**
 * A person's HR dates at a workplace (users/{uid}/hr/{wpId}). "Valid until" is the last day the
 * exam or training covers.
 */
data class StaffRecord(
    val userId: String,
    val workplaceId: String,
    /** The day of the last OHS training (initial or periodic). */
    val ohsTrainingDate: LocalDate? = null,
    val ohsValidUntil: LocalDate? = null,
    val medicalExamValidUntil: LocalDate? = null,
    val updatedAt: String? = null,
    val updatedBy: String? = null,
    /** SH-212: the day of the medical exam the current validity comes from, when known. */
    val medicalExamDate: LocalDate? = null,
    /**
     * SH-212: the earlier (or planned) trainings and exams, beside the current ones above, which
     * older app versions and functions/staffReadiness.js read. Dates only, at most [MAX_HISTORY].
     */
    val history: List<StaffCheckRecord> = emptyList()
) {
    fun validUntil(check: StaffCheck): LocalDate? = when (check) {
        StaffCheck.MEDICAL_EXAM -> medicalExamValidUntil
        StaffCheck.OHS_TRAINING -> ohsValidUntil
    }

    /** SH-212: the current [check] as a record (null: nothing recorded). */
    fun current(check: StaffCheck): StaffCheckRecord? = when (check) {
        StaffCheck.MEDICAL_EXAM -> StaffCheckRecord(check, medicalExamDate, medicalExamValidUntil)
        StaffCheck.OHS_TRAINING -> StaffCheckRecord(check, ohsTrainingDate, ohsValidUntil)
    }.takeIf { it.from != null || it.validUntil != null }

    /** SH-212: every [check] recorded, the current one included, by start (then validity). */
    fun records(check: StaffCheck): List<StaffCheckRecord> =
        (history.filter { it.check == check } + listOfNotNull(current(check)))
            .sortedWith(compareBy<StaffCheckRecord, LocalDate?>(nullsFirst()) { it.from }.thenBy(nullsFirst()) { it.validUntil })

    companion object {
        /** Trainings and exams kept besides the current ones (firestore.rules validStaffHr unrolls as many). */
        const val MAX_HISTORY = 10

        /**
         * SH-212: [records] written as a record: per check the current one is the latest started by
         * [today] (a record without a start counts as started), else the earliest planned one;
         * the others go to [history] (the most recent [MAX_HISTORY]).
         */
        fun of(userId: String, workplaceId: String, records: List<StaffCheckRecord>, today: LocalDate, base: StaffRecord? = null): StaffRecord {
            fun currentOf(check: StaffCheck): StaffCheckRecord? {
                val mine = records.filter { it.check == check }
                    .sortedWith(compareBy<StaffCheckRecord, LocalDate?>(nullsFirst()) { it.from }.thenBy(nullsFirst()) { it.validUntil })
                return mine.lastOrNull { it.from == null || it.from <= today } ?: mine.firstOrNull()
            }
            val ohs = currentOf(StaffCheck.OHS_TRAINING)
            val medical = currentOf(StaffCheck.MEDICAL_EXAM)
            val rest = records.filter { it !== ohs && it !== medical }
                .sortedWith(compareBy<StaffCheckRecord, LocalDate?>(nullsFirst()) { it.from ?: it.validUntil })
                .takeLast(MAX_HISTORY)
            return (base ?: StaffRecord(userId, workplaceId)).copy(
                ohsTrainingDate = ohs?.from,
                ohsValidUntil = ohs?.validUntil,
                medicalExamDate = medical?.from,
                medicalExamValidUntil = medical?.validUntil,
                history = rest
            )
        }
    }
}

/** SH-212: one OHS training or medical exam: the day it happened (when known) and the last day it covers. */
data class StaffCheckRecord(val check: StaffCheck, val from: LocalDate?, val validUntil: LocalDate?)

/**
 * Where a person's checks stand at a workplace. Read from the dates ([dates] known: the person,
 * owners and financial managers) or from the status document operational managers may read
 * ([dates] null: the statuses as of [checkedOn]).
 */
data class StaffReadiness(
    val userId: String,
    val workplaceId: String,
    val medical: CheckStatus,
    val ohs: CheckStatus,
    /** The day the statuses were worked out. */
    val checkedOn: LocalDate? = null,
    /** The dates behind the statuses, when the reader may see them. */
    val dates: StaffRecord? = null
) {
    fun status(check: StaffCheck): CheckStatus = when (check) {
        StaffCheck.MEDICAL_EXAM -> medical
        StaffCheck.OHS_TRAINING -> ohs
    }

    /**
     * The checks that have run out by [date]. Exact with [dates]; from the statuses alone, a
     * check EXPIRED stays expired (until someone records a new date), anything else is not known
     * to have run out.
     */
    fun expiredOn(date: LocalDate): Set<StaffCheck> = StaffCheck.entries.filterTo(linkedSetOf()) { check ->
        if (dates != null) StaffChecks.status(dates.validUntil(check), date) == CheckStatus.EXPIRED
        else status(check) == CheckStatus.EXPIRED
    }

    /** Something a manager should act on: a check expired or ending within the warning period. */
    val needsAttention: Boolean
        get() = medical in ATTENTION || ohs in ATTENTION

    val anyExpired: Boolean
        get() = medical == CheckStatus.EXPIRED || ohs == CheckStatus.EXPIRED

    private companion object {
        val ATTENTION = setOf(CheckStatus.EXPIRING, CheckStatus.EXPIRED)
    }
}

object StaffChecks {
    /** Expiry warnings start this many days ahead. */
    const val WARNING_DAYS = 30

    /** Free-text availability notes are kept short (firestore.rules checks the same). */
    const val NOTES_MAX_LENGTH = 1000

    /**
     * [validUntil] on [on]: MISSING without a date, EXPIRED after it, EXPIRING within
     * [warningDays] days (the day itself included), else VALID.
     */
    fun status(validUntil: LocalDate?, on: LocalDate, warningDays: Int = WARNING_DAYS): CheckStatus = when {
        validUntil == null -> CheckStatus.MISSING
        validUntil < on -> CheckStatus.EXPIRED
        validUntil <= on.plus(DatePeriod(days = warningDays)) -> CheckStatus.EXPIRING
        else -> CheckStatus.VALID
    }

    /** [record] (null: nothing recorded) as statuses on [today], keeping the dates. */
    fun readiness(record: StaffRecord?, userId: String, workplaceId: String, today: LocalDate): StaffReadiness =
        StaffReadiness(
            userId = userId,
            workplaceId = workplaceId,
            medical = status(record?.medicalExamValidUntil, today),
            ohs = status(record?.ohsValidUntil, today),
            checkedOn = today,
            dates = record
        )

    /** [readiness] without the dates: what the status document operational managers read holds. */
    fun statusOnly(readiness: StaffReadiness): StaffReadiness = readiness.copy(dates = null)
}

/** The checks [record] gets wrong: a training "valid until" before the training itself. */
fun StaffRecord.problems(): Set<StaffRecordProblem> = buildSet {
    if (ohsTrainingDate != null && ohsValidUntil != null && ohsValidUntil < ohsTrainingDate) add(StaffRecordProblem.OHS_VALID_BEFORE_TRAINING)
}

enum class StaffRecordProblem { OHS_VALID_BEFORE_TRAINING }

/** A staff record the use case refuses to save (the UI shows the [problems] at the fields). */
class InvalidStaffRecordException(val problems: Set<StaffRecordProblem>) : IllegalArgumentException("Invalid staff record: $problems")

// --- Shift leads and supervision ----------------------------------------------------------------

/**
 * SH-104: who leads shifts at a workplace: its owner, anyone with a manager permission, and anyone
 * whose position ([WorkplaceSettings.leadRoles]) or one of whose skills
 * ([WorkplaceSettings.leadSkills]) the workplace marks as leading (names compared trimmed,
 * ignoring case). There is no per-person flag any more.
 */
data class ShiftLeadRule(
    val ownerId: String? = null,
    val leadRoles: Collection<String> = emptyList(),
    val leadSkills: Collection<String> = emptyList()
) {
    private val roles = leadRoles.mapNotNullTo(HashSet()) { it.key() }
    private val skills = leadSkills.mapNotNullTo(HashSet()) { it.key() }

    /** Whether the workplace marks any position or skill as leading. */
    val marksAny: Boolean get() = roles.isNotEmpty() || skills.isNotEmpty()

    /** Whether [userId] with [engagement] (null: unknown here) leads shifts. */
    fun leads(engagement: Engagement?, userId: String? = null): Boolean {
        if (userId != null && userId == ownerId) return true
        engagement ?: return false
        return engagement.permission != UserPermission.EMPLOYEE ||
            engagement.role?.key()?.let { it in roles } == true ||
            engagement.skills.any { skill -> skill.key()?.let { it in skills } == true }
    }

    /** Needs supervision and does not lead shifts (a lead is never "needs supervision"). */
    fun needsSupervision(engagement: Engagement?, userId: String? = null): Boolean =
        engagement?.competence == Competence.NEEDS_SUPERVISION && !leads(engagement, userId)

    companion object {
        private fun String.key(): String? = trim().lowercase().takeIf { it.isNotEmpty() }

        /** The rule [workplace] sets (null: only managers lead). */
        fun of(workplace: Workplace?): ShiftLeadRule = workplace?.let {
            ShiftLeadRule(it.ownerId, it.settings.leadRoles, it.settings.leadSkills)
        } ?: ShiftLeadRule()
    }
}

/** What a roster day is missing (soft: the manager sees it, nothing is refused). */
enum class DayStaffWarning {
    /** People work, none of them leads shifts, and the workplace marks lead positions or skills. */
    NO_SHIFT_LEAD,
    /** At some time of the day only people who need supervision work ([DayStaffAlert.windows]). */
    ONLY_NEEDS_SUPERVISION
}

/**
 * A roster day's warning. For [DayStaffWarning.ONLY_NEEDS_SUPERVISION]: the [windows] when only
 * people who need supervision work and the [shiftIds] of theirs that fall in one.
 */
data class DayStaffAlert(
    val warning: DayStaffWarning,
    val windows: List<TimeWindow> = emptyList(),
    val shiftIds: Set<String> = emptySet()
)

object DayStaffing {
    /**
     * Each day's warning over a roster's [shifts] (planned times), given the people's engagements
     * at the workplace ([engagements]: uid → engagement) and its [leadRule]:
     * - "only people who need supervision" when, at any moment of the day, everyone working needs
     *   supervision ([SupervisionCheck], the auto-scheduler's rule 7); shifts of the next or
     *   previous day across midnight count for the time they overlap;
     * - else "no shift lead" when people work, none of them leads, and the workplace marks lead
     *   positions or skills at all, so one that does not use the marks is not warned every day.
     * Nobody working: nothing to warn about.
     */
    fun warnings(
        shifts: Collection<Shift>,
        engagements: Map<String, Engagement?>,
        leadRule: ShiftLeadRule
    ): Map<String, DayStaffAlert> {
        val assigned = shifts.filter { it.userId != null }
        val spans = SupervisionCheck.spans(assigned)
        val windows = SupervisionCheck.uncoveredWindows(spans) { uid -> leadRule.needsSupervision(engagements[uid], uid) }
        return assigned.groupBy { it.date.take(10) }.mapNotNull { (date, dayShifts) ->
            val ids = dayShifts.mapTo(HashSet()) { it.id }
            val daySpans = spans.filter { it.shiftId in ids }
            val hit = windows.filter { w -> daySpans.any { it.overlaps(w) } }
            if (hit.isNotEmpty()) {
                val flagged = daySpans.filter { s -> hit.any { s.overlaps(it) } }.mapNotNullTo(LinkedHashSet()) { it.shiftId }
                return@mapNotNull date to DayStaffAlert(DayStaffWarning.ONLY_NEEDS_SUPERVISION, hit, flagged)
            }
            val working = dayShifts.mapNotNull { it.userId }.distinct()
            if (leadRule.marksAny && working.none { leadRule.leads(engagements[it], it) }) {
                return@mapNotNull date to DayStaffAlert(DayStaffWarning.NO_SHIFT_LEAD)
            }
            null
        }.toMap()
    }
}
